import React, { useRef, useState, useEffect } from 'react';
import { FiCamera, FiScissors, FiRefreshCw } from 'react-icons/fi';
import './ImageUpload.css';

/**
 * ImageUpload Component
 * Handles image file selection and preview
 */
const ImageUpload = ({ onImageSelect, selectedImage, onCropClick }) => {
  const fileInputRef = useRef(null);
  const urlRef = useRef(null);
  const [dragActive, setDragActive] = useState(false);
  const [previewUrl, setPreviewUrl] = useState(null);

  useEffect(() => {
    if (!selectedImage) {
      if (urlRef.current) {
        URL.revokeObjectURL(urlRef.current);
        urlRef.current = null;
      }
      setPreviewUrl(null);
      return;
    }
    const url = URL.createObjectURL(selectedImage);
    urlRef.current = url;
    setPreviewUrl(url);
    return () => {
      URL.revokeObjectURL(url);
      urlRef.current = null;
      setPreviewUrl(null);
    };
  }, [selectedImage]);

  /**
   * Handle file selection
   */
  const handleFileSelect = (file) => {
    if (file && file.type.startsWith('image/')) {
      onImageSelect(file);
    } else {
      alert('Please select a valid image file (JPG, PNG, or WEBP)');
    }
  };

  /**
   * Handle file input change
   */
  const handleChange = (e) => {
    const file = e.target.files?.[0];
    if (file) {
      handleFileSelect(file);
    }
  };

  /**
   * Handle drag and drop
   */
  const handleDrag = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === 'dragenter' || e.type === 'dragover') {
      setDragActive(true);
    } else if (e.type === 'dragleave') {
      setDragActive(false);
    }
  };

  /**
   * Handle drop
   */
  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);

    const file = e.dataTransfer.files?.[0];
    if (file) {
      handleFileSelect(file);
    }
  };

  /**
   * Trigger file input click
   */
  const handleClick = () => {
    fileInputRef.current?.click();
  };

  return (
    <div className="image-upload-container">
      <h2 className="section-title">Upload Your Photo</h2>
      <input
        ref={fileInputRef}
        type="file"
        accept="image/*"
        onChange={handleChange}
        className="file-input"
        style={{ display: 'none' }}
      />

      {!selectedImage ? (
        <div
          className={`upload-area ${dragActive ? 'drag-active' : ''}`}
          onDragEnter={handleDrag}
          onDragLeave={handleDrag}
          onDragOver={handleDrag}
          onDrop={handleDrop}
          onClick={handleClick}
        >
          <div className="upload-icon">
            <FiCamera />
          </div>
          <p className="upload-text">
            Click to upload or drag and drop
          </p>
          <p className="upload-hint">
            Supports JPG, PNG, WEBP
          </p>
        </div>
      ) : (
        <div className="image-preview-container">
          <div className="preview-image-wrapper">
            <img
              src={previewUrl}
              alt="Selected"
              className="preview-image"
            />
            <div className="image-overlay-actions">
              <button
                className="action-btn crop-btn"
                onClick={onCropClick}
                title="Crop Image"
              >
                <FiScissors /> <span>Crop</span>
              </button>
              <button
                className="action-btn change-btn"
                onClick={handleClick}
                title="Change Image"
              >
                <FiRefreshCw /> <span>Change</span>
              </button>
            </div>
          </div>
          <div className="image-info">
            <p className="image-name">{selectedImage.name}</p>
            <p className="image-size">{(selectedImage.size / 1024 / 1024).toFixed(2)} MB</p>
          </div>
        </div>
      )}
    </div>
  );
};

export default ImageUpload;



